import { useState, useRef } from "react";
import { MoveHorizontal } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/site/Primitives";
import { RESULTS } from "@/data/site";

const BeforeAfter = ({ before, after }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const move = (clientX) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  };

  return (
    <div
      ref={ref}
      data-testid="before-after"
      className="relative w-full aspect-square sm:aspect-[4/5] overflow-hidden rounded-[2rem] ring-1 ring-gold/25 select-none cursor-ew-resize"
      onMouseMove={(e) => dragging.current && move(e.clientX)}
      onMouseDown={(e) => { dragging.current = true; move(e.clientX); }}
      onMouseUp={() => (dragging.current = false)}
      onMouseLeave={() => (dragging.current = false)}
      onTouchMove={(e) => move(e.touches[0].clientX)}
    >
      {/* After (full) */}
      <img src={after} alt="Nachher" className="absolute inset-0 h-full w-full object-cover" draggable={false} />
      <span className="absolute top-4 right-4 bg-charcoal/70 text-cream text-[11px] uppercase tracking-widest px-3 py-1 rounded-full">Nachher</span>
      {/* Before (clipped) */}
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img src={before} alt="Vorher" className="absolute inset-0 h-full w-full object-cover" style={{ width: `${100 / (pos / 100)}%`, maxWidth: "none" }} draggable={false} />
        <span className="absolute top-4 left-4 bg-cream/80 text-charcoal text-[11px] uppercase tracking-widest px-3 py-1 rounded-full">Vorher</span>
      </div>
      {/* Handle */}
      <div className="absolute top-0 bottom-0" style={{ left: `${pos}%` }}>
        <div className="absolute top-0 bottom-0 -translate-x-1/2 w-0.5 bg-cream/90" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-cream shadow-lg flex items-center justify-center">
          <MoveHorizontal size={20} className="text-charcoal" />
        </div>
      </div>
    </div>
  );
};

export const Results = () => {
  const r = RESULTS[0];
  return (
    <section id="ergebnisse" className="relative py-24 lg:py-36 bg-cream-soft">
      <div className="mx-auto max-w-6xl px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal>
          <div className="max-w-md">
            <Eyebrow>Ergebnisse</Eyebrow>
            <h2 className="mt-6 font-serif font-light tracking-tight text-charcoal text-4xl sm:text-5xl leading-tight">
              Natürlich schön – sichtbar gemacht
            </h2>
            <p className="mt-6 text-charcoal-soft leading-relaxed">{r.note}</p>
            <p className="mt-4 text-sm text-gold-deep uppercase tracking-widest">{r.label}</p>
            <p className="mt-8 text-xs text-charcoal-muted leading-relaxed">
              Ziehen Sie den Regler, um Vorher und Nachher zu vergleichen. Alle Ergebnisse sind individuell und stellen kein Heilversprechen dar.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <BeforeAfter before={r.before} after={r.after} />
        </Reveal>
      </div>
    </section>
  );
};
